import React from "react";
import Link from "@amzn/awsui-components-react/polaris/link";
import type { LinkProps } from "@amzn/awsui-components-react/polaris/link";
import { useRouter, useTelemetry } from "@amzn/xb-hooks";
import type { TelemetryData } from "./types";

type SoftLinkProps = Omit<LinkProps, "href"> & {
  href: string; // This makes href required
  telemetry?: TelemetryData;
};

/**
 * SoftLink is a client-routing based wrapper around the Cloudscape Link component. It maintains all of the
 * functionality/API of the base Cloudscape Link component while handling in-app navigation without full page reloads.
 *
 * @param {string} href - The destination path for the link (required)
 * @param {function} [onFollow] - Optional callback function when link is clicked. Overrides default routing behavior
 * @param {object} [telemetry] - Optional telemetry data for tracking link clicks with eventId and optional eventDetails
 *
 * @see {@link https://cloudscape.design/components/link/?tabId=api|AWS CLoudscape Link API}
 */
export const SoftLink: React.FC<SoftLinkProps> = ({ href, telemetry, onFollow, children, ...rest }) => {
  const { push } = useRouter();
  const { trackEvent } = useTelemetry();

  const handleFollow = (event: CustomEvent) => {
    event.preventDefault();
    if (telemetry?.eventId) {
      trackEvent("click", telemetry.eventId, {
        data: { ...telemetry.eventDetails },
      });
    }
    onFollow ? onFollow(event) : push(href);
  };

  return (
    <Link href={href} onFollow={handleFollow} {...rest}>
      {children}
    </Link>
  );
};
